import { Loading } from "../app/Status";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent, type ReactNode } from "react";
import { Link, useNavigate, useParams } from "react-router";
import {
  formatEuro,
  INVOICE_STATUS_LABELS,
  MODULE_DESCRIPTIONS,
  MODULE_LABELS,
  MODULE_TRIAL_DAYS,
  ORG_MODULE_STATUS_LABELS,
  ORG_MODULE_STATUSES,
  ORG_STATUS_LABELS,
  ORG_STATUSES,
  REPORT_BRAND_LABELS,
  REPORT_BRANDS,
  TRIAL_DAYS,
  type BillingDetails,
  type BuildingTransferResult,
  type PlatformBuildingRow,
  type InvoiceRow,
  type OrgModuleStatus,
  type OrgStatus,
  type PlatformOrgDetail,
  type ReportBrand,
  type PlatformOrgRow,
} from "@klantpunt/shared";
import { IconChevronLeft, IconChevronRight, IconPlus, IconReceipt, IconTrash } from "../app/icons";
import { Meta } from "../app/Meta";
import { Modal } from "../app/Modal";
import { api, ApiError } from "../lib/api";
import { meKey, useMe } from "../lib/auth";
import { PlatformHeader } from "../features/platform/PlatformHeader";
import { useToast } from "../lib/toast";

const errMsg = (e: unknown) => (e instanceof ApiError ? e.message : "Er ging iets mis");
const fmtDate = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString("nl-NL", { day: "numeric", month: "short", year: "numeric" }) : "—";
const isoDay = (d: Date) => d.toISOString().slice(0, 10);

/** "12,50" of "12.50" → 1250 centen; null bij ongeldige invoer. */
export function parseEuro(v: string): number | null {
  const s = v
    .trim()
    .replace(/\s|€/g, "")
    .replace(/\.(?=\d{3}(\D|$))/g, "")
    .replace(",", ".");
  if (!/^\d+(\.\d{1,2})?$/.test(s)) return null;
  return Math.round(Number(s) * 100);
}
const euroInput = (c: number) => (c / 100).toFixed(2).replace(".", ",");
const domainsText = (d: string[]) => d.join(", ");
const parseDomains = (v: string) =>
  v
    .split(/[\s,;]+/)
    .map((x) => x.trim().toLowerCase())
    .filter(Boolean);

function StatusChip({ status, readOnly }: { status: OrgStatus; readOnly: boolean }) {
  const label = status === "trial" && readOnly ? "Proef verlopen" : ORG_STATUS_LABELS[status];
  return <span className={`chip org-${readOnly ? "ro" : status}`}>{label}</span>;
}

/** Alleen voor platformbeheerders; anderen krijgen "bestaat niet" (zoals de API). */
export function PlatformGuard({ children }: { children: ReactNode }) {
  const { data: me } = useMe();
  if (!me?.user.platformAdmin)
    return (
      <div className="center">
        <p>Deze pagina bestaat niet.</p>
      </div>
    );
  return <>{children}</>;
}

export function PlatformPage() {
  return (
    <PlatformGuard>
      <PlatformOrgs />
    </PlatformGuard>
  );
}

export function PlatformOrgPage() {
  return (
    <PlatformGuard>
      <PlatformOrg />
    </PlatformGuard>
  );
}

function PlatformOrgs() {
  const q = useQuery({ queryKey: ["platform", "orgs"], queryFn: () => api<PlatformOrgRow[]>("/platform/orgs") });
  const [creating, setCreating] = useState(false);
  const rows = q.data ?? [];
  const monthly = rows.reduce((s, r) => s + (r.readOnly ? 0 : r.monthlyCents), 0);
  const open = rows.reduce((s, r) => s + r.openInvoiceCents, 0);

  return (
    <div className="page platform">
      <PlatformHeader
        title="Organisaties"
        action={
          <button type="button" className="btn primary" onClick={() => setCreating(true)}>
            <IconPlus size={20} aria-hidden="true" />
            Nieuwe organisatie
          </button>
        }
      />
      <div className="pf-kpis" role="group" aria-label="Totalen">
        <span className="pf-kpi">
          <span className="label">Organisaties</span>
          <strong className="num">{rows.length}</strong>
        </span>
        <span className="pf-kpi">
          <span className="label">Per maand (excl. btw)</span>
          <strong className="num" data-testid="kpi-monthly">
            {formatEuro(monthly)}
          </strong>
        </span>
        <span className="pf-kpi">
          <span className="label">Openstaand</span>
          <strong className="num">{formatEuro(open)}</strong>
        </span>
      </div>
      <section className="panel" aria-busy={q.isPending}>
        {q.isPending ? (
          <Loading />
        ) : (
          <ul className="list">
            {rows.map((o) => (
              <li key={o.id}>
                <Link to={`/platform/${o.id}`} className="buildinglink">
                  <span className="grow">
                    <strong>{o.name}</strong>
                    <span className="muted small block">
                      {o.buildings} {o.buildings === 1 ? "gebouw" : "gebouwen"} · {o.members}{" "}
                      {o.members === 1 ? "gebruiker" : "gebruikers"} · {formatEuro(o.monthlyCents)} p/m
                      {o.status === "trial" && !o.readOnly && ` · proef tot ${fmtDate(o.trialEndsAt)}`}
                      {o.openInvoiceCents > 0 && ` · open ${formatEuro(o.openInvoiceCents)}`}
                    </span>
                  </span>
                  <StatusChip status={o.status} readOnly={o.readOnly} />
                  <IconChevronRight size={20} aria-hidden="true" className="muted" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </section>
      {creating && <CreateOrgDialog onClose={() => setCreating(false)} />}
    </div>
  );
}

function CreateOrgDialog({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const toast = useToast();
  const [name, setName] = useState("");
  const [slug, setSlug] = useState("");
  const [slugTouched, setSlugTouched] = useState(false);
  const [ownerEmail, setOwnerEmail] = useState("");
  const [domains, setDomains] = useState("");
  const [trial, setTrial] = useState(true);
  const [onboarding, setOnboarding] = useState(true);
  const [price, setPrice] = useState("0,00");
  const [error, setError] = useState<string | null>(null);
  const autoSlug = (v: string) =>
    v
      .toLowerCase()
      .normalize("NFD")
      .replace(/[̀-ͯ]/g, "")
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "")
      .slice(0, 40);

  const create = useMutation({
    mutationFn: () => {
      const cents = parseEuro(price);
      if (cents === null) throw new ApiError(400, "price", "Ongeldige prijs");
      return api<{ org: PlatformOrgDetail; mailed: boolean }>("/platform/orgs", {
        method: "POST",
        body: {
          name,
          slug,
          ownerEmail,
          allowedDomains: parseDomains(domains),
          trialDays: trial ? TRIAL_DAYS : 0,
          pricePerBuildingCents: cents,
          onboarding,
        },
      });
    },
    onSuccess: (r) => {
      void qc.invalidateQueries({ queryKey: ["platform"] });
      toast(
        r.mailed
          ? `${r.org.name} aangemaakt; uitnodiging gestuurd naar ${ownerEmail}`
          : `${r.org.name} aangemaakt; ${ownerEmail} kan inloggen met Google`,
      );
      navigate(`/platform/${r.org.id}`);
    },
    onError: (e) => setError(errMsg(e)),
  });

  function submit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    create.mutate();
  }

  return (
    <Modal
      title="Nieuwe organisatie"
      onClose={onClose}
      busy={create.isPending}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="create-org" className="btn primary" disabled={create.isPending}>
            Aanmaken
          </button>
        </>
      }
    >
      <form id="create-org" className="stack" onSubmit={submit}>
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Naam *</span>
          <input
            required
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              if (!slugTouched) setSlug(autoSlug(e.target.value));
            }}
            placeholder="Vastgoed X"
          />
        </label>
        <label className="field">
          <span className="label">Korte naam *</span>
          <input
            required
            value={slug}
            onChange={(e) => {
              setSlugTouched(true);
              setSlug(e.target.value);
            }}
            pattern="[a-z0-9](?:[a-z0-9\-]{0,38}[a-z0-9])?"
            title="kleine letters, cijfers en '-'"
          />
          <span className="muted small">Kleine letters, cijfers en '-'; later niet te wijzigen.</span>
        </label>
        <label className="field">
          <span className="label">E-mailadres eerste eigenaar *</span>
          <input
            type="email"
            required
            value={ownerEmail}
            onChange={(e) => setOwnerEmail(e.target.value)}
            placeholder="naam@bedrijf.nl"
          />
        </label>
        <label className="field">
          <span className="label">Toegestane domeinen (optioneel)</span>
          <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="bedrijf.nl" />
          <span className="muted small">
            Iedereen met een Google-account op dit domein komt binnen als inspecteur. Leeg = alleen uitgenodigde
            adressen.
          </span>
        </label>
        <label className="field">
          <span className="label">Prijs per gebouw per maand (excl. btw)</span>
          <input inputMode="decimal" value={price} onChange={(e) => setPrice(e.target.value)} />
        </label>
        <label className="check">
          <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} />
          <span>Proefperiode van {TRIAL_DAYS} dagen</span>
        </label>
        <label className="check">
          <input type="checkbox" checked={onboarding} onChange={(e) => setOnboarding(e.target.checked)} />
          <span>Welkomstscherm en checklist Aan de slag voor de eigenaar</span>
        </label>
      </form>
    </Modal>
  );
}

function PlatformOrg() {
  const { id = "" } = useParams();
  const key = ["platform", "org", id];
  const qc = useQueryClient();
  const toast = useToast();
  const q = useQuery({ queryKey: key, queryFn: () => api<PlatformOrgDetail>(`/platform/orgs/${id}`) });
  const [invoicing, setInvoicing] = useState(false);
  const setOrg = (o: PlatformOrgDetail) => {
    qc.setQueryData(key, o);
    void qc.invalidateQueries({ queryKey: ["platform", "orgs"] });
  };
  const patch = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api<PlatformOrgDetail>(`/platform/orgs/${id}`, { method: "PATCH", body }),
    onSuccess: (o) => {
      setOrg(o);
      toast("Opgeslagen");
    },
    onError: (e) => toast(errMsg(e)),
  });
  const join = useMutation({
    mutationFn: () => api<PlatformOrgDetail>(`/platform/orgs/${id}/join`, { method: "POST" }),
    onSuccess: (o) => {
      setOrg(o);
      void qc.invalidateQueries({ queryKey: meKey });
      toast(`Je bent nu eigenaar van ${o.name}; wissel via het profielmenu`);
    },
    onError: (e) => toast(errMsg(e)),
  });

  if (q.isPending) return <Loading page />;
  if (!q.data) return <div className="center">Organisatie niet gevonden.</div>;
  const o = q.data;

  return (
    <div className="page platform">
      <Link to="/platform" className="backlink">
        <IconChevronLeft size={18} aria-hidden="true" />
        Organisaties
      </Link>
      <div className="titlerow">
        <h1 className="dot">{o.name}</h1>
        <StatusChip status={o.status} readOnly={o.readOnly} />
      </div>
      <p className="muted">
        {o.slug} · aangemaakt {fmtDate(o.createdAt)} · {o.buildings} {o.buildings === 1 ? "gebouw" : "gebouwen"} ·{" "}
        {o.members} {o.members === 1 ? "gebruiker" : "gebruikers"}
      </p>

      <SubscriptionPanel key={`s-${o.id}`} o={o} busy={patch.isPending} onSave={(b) => patch.mutate(b)} />
      <ModulesPanel key={`m-${o.id}`} o={o} onSaved={setOrg} />
      <InvoicesPanel
        o={o}
        onNew={() => setInvoicing(true)}
        onChanged={() => void qc.invalidateQueries({ queryKey: ["platform"] })}
      />
      <BillingPanel
        key={`b-${o.id}`}
        billing={o.billing}
        busy={patch.isPending}
        onSave={(billing) => patch.mutate({ billing })}
      />
      <AccessPanel key={`a-${o.id}`} o={o} busy={patch.isPending} onSave={(b) => patch.mutate(b)} onInvited={setOrg} />
      <BuildingsPanel o={o} />

      <section className="panel" aria-labelledby="pf-support">
        <h2 id="pf-support">Ondersteuning</h2>
        {o.iAmMember ? (
          <p className="muted">Je bent lid van deze organisatie; wissel via het profielmenu.</p>
        ) : (
          <div className="profile-row pf-wrap">
            <span className="grow muted small">
              Voeg jezelf toe als eigenaar om mee te kijken of te helpen. Dit wordt vastgelegd in het logboek van de
              organisatie.
            </span>
            <button type="button" className="btn" onClick={() => join.mutate()} disabled={join.isPending}>
              Mij toevoegen als eigenaar
            </button>
          </div>
        )}
      </section>

      {invoicing && (
        <InvoiceDialog
          o={o}
          onClose={() => setInvoicing(false)}
          onSaved={() => {
            setInvoicing(false);
            void qc.invalidateQueries({ queryKey: ["platform"] });
          }}
        />
      )}
    </div>
  );
}

/** Gebouwen van de organisatie, met "Overdragen" naar een andere organisatie. */
function BuildingsPanel({ o }: { o: PlatformOrgDetail }) {
  const q = useQuery({
    queryKey: ["platform", "org", o.id, "buildings"],
    queryFn: () => api<PlatformBuildingRow[]>(`/platform/orgs/${o.id}/buildings`),
  });
  const [search, setSearch] = useState("");
  const [moving, setMoving] = useState<PlatformBuildingRow | null>(null);
  const term = search.trim().toLowerCase();
  const list = (q.data ?? []).filter(
    (b) => !term || `${b.code} ${b.name} ${b.address ?? ""} ${b.clientName}`.toLowerCase().includes(term),
  );
  return (
    <section className="panel" aria-labelledby="pf-buildings">
      <h2 id="pf-buildings">Gebouwen</h2>
      <p className="muted small">
        Draag een gebouw over aan een andere organisatie, bijvoorbeeld als het gebouw van beheerder wisselt. Alles in
        het gebouw gaat mee.
      </p>
      {(q.data?.length ?? 0) > 8 && (
        <input
          type="search"
          className="pf-search"
          placeholder="Zoek op code, naam, adres of klant"
          aria-label="Gebouwen zoeken"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      )}
      {q.isPending ? (
        <Loading />
      ) : !q.data?.length ? (
        <p className="muted">Nog geen gebouwen.</p>
      ) : !list.length ? (
        <p className="muted">Geen gebouwen gevonden.</p>
      ) : (
        <ul className="pf-blist">
          {list.map((b) => (
            <li key={b.id}>
              <span className="code">{b.code}</span>
              <span className="grow">
                {b.name}
                {b.archived && <span className="chip st-gesloten pf-chip">Gearchiveerd</span>}
                <Meta className="muted small" items={[b.address, b.clientName]} />
              </span>
              <button
                type="button"
                className="btn small"
                aria-label={`${b.code} overdragen`}
                onClick={() => setMoving(b)}
              >
                Overdragen
              </button>
            </li>
          ))}
        </ul>
      )}
      {moving && <TransferDialog from={o} building={moving} onClose={() => setMoving(null)} />}
    </section>
  );
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

/** Wat er meegaat, in gewone woorden; nullen weggelaten. */
function transferLines(r: BuildingTransferResult): string[] {
  const c = r.counts;
  return [
    c.floors && plural(c.floors, "verdieping", "verdiepingen"),
    c.assets && plural(c.assets, "asset", "assets"),
    c.issues && plural(c.issues, "bevinding", "bevindingen"),
    c.tasks && plural(c.tasks, "taak", "taken"),
    c.obligations && plural(c.obligations, "keuring", "keuringen"),
    c.budgetItems && plural(c.budgetItems, "MJOP-post", "MJOP-posten"),
    c.assessments && plural(c.assessments, "conditiemeting", "conditiemetingen"),
    c.meters && plural(c.meters, "meter", "meters"),
    c.intake && plural(c.intake, "melding", "meldingen"),
    c.documents && plural(c.documents, "document in het dossier", "documenten in het dossier"),
    c.files && plural(c.files, "bestand", "bestanden"),
    c.contacts && plural(c.contacts, "koppeling uit het adresboek", "koppelingen uit het adresboek"),
  ].filter((x): x is string => Boolean(x));
}

function TransferDialog({
  from,
  building,
  onClose,
}: {
  from: PlatformOrgDetail;
  building: PlatformBuildingRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const orgs = useQuery({ queryKey: ["platform", "orgs"], queryFn: () => api<PlatformOrgRow[]>("/platform/orgs") });
  const targets = (orgs.data ?? []).filter((x) => x.id !== from.id);
  const [toOrgId, setToOrgId] = useState("");
  const [code, setCode] = useState(building.code);
  const [typed, setTyped] = useState("");
  const [error, setError] = useState<string | null>(null);
  const cleanCode = code.trim().toUpperCase();
  const check = useQuery({
    queryKey: ["platform", "transfer-check", building.id, toOrgId, cleanCode],
    queryFn: () =>
      api<BuildingTransferResult>(`/platform/buildings/${building.id}/transfer`, {
        method: "POST",
        body: { toOrgId, code: cleanCode },
      }),
    enabled: Boolean(toOrgId) && /^[A-Z0-9]{2,8}$/.test(cleanCode),
    retry: false,
  });
  const target = targets.find((x) => x.id === toOrgId);
  const r = check.data;
  const blocked = !r || r.codeTaken || r.qrConflicts.length > 0;
  const confirmed = typed.trim().toUpperCase() === building.code.toUpperCase();
  const save = useMutation({
    mutationFn: () =>
      api<BuildingTransferResult>(`/platform/buildings/${building.id}/transfer`, {
        method: "POST",
        body: { toOrgId, code: cleanCode, confirm: typed.trim() },
      }),
    onSuccess: () => {
      toast(`${building.name} overgedragen aan ${target?.name ?? "de andere organisatie"}`);
      void qc.invalidateQueries({ queryKey: ["platform"] });
      onClose();
    },
    onError: (e) => setError(errMsg(e)),
  });
  const lines = r ? transferLines(r) : [];
  return (
    <Modal
      title={`${building.code} overdragen`}
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button
            type="button"
            className="btn primary danger"
            onClick={() => {
              setError(null);
              save.mutate();
            }}
            disabled={blocked || !confirmed || save.isPending || check.isFetching}
          >
            {save.isPending ? "Overdragen…" : "Overdragen"}
          </button>
        </>
      }
    >
      <div className="stack">
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <p className="muted small">
          {building.name} gaat van {from.name} naar een andere organisatie, met alles erin. Daar komt het onder{" "}
          <em>Nog in te delen</em>.
        </p>
        <label className="field">
          <span className="label">Naar organisatie</span>
          <select value={toOrgId} onChange={(e) => setToOrgId(e.target.value)} required>
            <option value="">Kies een organisatie</option>
            {targets.map((x) => (
              <option key={x.id} value={x.id}>
                {x.name}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span className="label">Gebouwcode in de nieuwe organisatie</span>
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            autoComplete="off"
            maxLength={8}
            style={{ textTransform: "uppercase" }}
          />
        </label>
        {check.isError && (
          <div className="alert" role="alert">
            {errMsg(check.error)}
          </div>
        )}
        {r?.codeTaken && (
          <div className="note warn" role="alert">
            Code {r.code} bestaat al bij {target?.name}. Kies een andere code; de bevindingcodes veranderen mee.
          </div>
        )}
        {r && r.qrConflicts.length > 0 && (
          <div className="note warn" role="alert">
            Deze QR-codes zijn bij {target?.name} al in gebruik: {r.qrConflicts.join(", ")}. Ontkoppel ze eerst.
          </div>
        )}
        {r && !blocked && (
          <>
            <div className="note">
              <strong>Gaat mee</strong>
              <br />
              {lines.length ? lines.join(", ") : "Alleen het gebouw (het is nog leeg)"}.
            </div>
            <div className="note danger-note">
              <strong>Vervalt</strong>
              <ul className="pf-list">
                {r.removedMembers > 0 && (
                  <li>
                    Gebouwtoegang van {plural(r.removedMembers, "medewerker", "medewerkers")} van {from.name}.
                  </li>
                )}
                {r.unassignedTasks > 0 && (
                  <li>
                    Toewijzing van {plural(r.unassignedTasks, "taak", "taken")} aan iemand die geen lid is van{" "}
                    {target?.name}; de naam blijft als tekst staan.
                  </li>
                )}
                {r.robawsLinks > 0 && <li>{plural(r.robawsLinks, "Robaws-koppeling", "Robaws-koppelingen")}.</li>}
                <li>Klant en locatie: het gebouw komt bij {target?.name} onder Nog in te delen.</li>
              </ul>
              Taken en meldingen krijgen een nieuw nummer bij {target?.name}. Dit kan niet ongedaan worden gemaakt.
            </div>
            <label className="field">
              <span className="label">Typ {building.code} om te bevestigen</span>
              <input
                value={typed}
                onChange={(e) => setTyped(e.target.value)}
                autoComplete="off"
                style={{ textTransform: "uppercase" }}
              />
            </label>
          </>
        )}
      </div>
    </Modal>
  );
}

/** Modules per organisatie: aan, proef (tot datum) of uit, met prijs per actief gebouw per maand. */
function ModulesPanel({ o, onSaved }: { o: PlatformOrgDetail; onSaved: (o: PlatformOrgDetail) => void }) {
  const toast = useToast();
  const [rows, setRows] = useState(() =>
    o.modules.map((m) => ({
      module: m.module,
      status: m.status,
      trialEndsAt: m.trialEndsAt ? m.trialEndsAt.slice(0, 10) : "",
      price: m.pricePerBuildingCents != null ? euroInput(m.pricePerBuildingCents) : "",
      cap: m.cap,
    })),
  );
  const save = useMutation({
    mutationFn: () =>
      api<PlatformOrgDetail>(`/platform/orgs/${o.id}/modules`, {
        method: "PUT",
        body: {
          modules: rows.map((r) => ({
            module: r.module,
            status: r.status,
            trialEndsAt: r.status === "trial" && r.trialEndsAt ? r.trialEndsAt : null,
            pricePerBuildingCents: r.price.trim() ? parseEuro(r.price) : null,
          })),
        },
      }),
    onSuccess: (d) => {
      onSaved(d);
      toast("Modules opgeslagen");
    },
    onError: (e) => toast(errMsg(e)),
  });
  const badPrice = rows.some((r) => r.price.trim() && parseEuro(r.price) === null);
  const set = (i: number, patch: Partial<(typeof rows)[number]>) =>
    setRows(rows.map((r, k) => (k === i ? { ...r, ...patch } : r)));
  return (
    <section className="panel" aria-labelledby="pf-modules">
      <h2 id="pf-modules">Modules</h2>
      <p className="muted small">
        Elke module heeft een eigen prijs per actief gebouw per maand (handmatige facturatie). Proef zonder datum = {""}
        {MODULE_TRIAL_DAYS} dagen vanaf opslaan; na de proef kan de organisatie de module alleen nog bekijken.
      </p>
      <div className="tscroll">
        <table className="it">
          <thead>
            <tr>
              <th>Module</th>
              <th>Stand</th>
              <th>Proef tot</th>
              <th>Prijs per gebouw p/m (€)</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={r.module}>
                <td>
                  <strong>{MODULE_LABELS[r.module]}</strong>
                  <span className="muted small block">{MODULE_DESCRIPTIONS[r.module]}</span>
                </td>
                <td>
                  <select
                    aria-label={`Stand ${MODULE_LABELS[r.module]}`}
                    value={r.status}
                    disabled={r.module === "buildings"}
                    onChange={(e) => set(i, { status: e.target.value as OrgModuleStatus })}
                  >
                    {ORG_MODULE_STATUSES.map((st) => (
                      <option key={st} value={st}>
                        {ORG_MODULE_STATUS_LABELS[st]}
                      </option>
                    ))}
                  </select>
                  {r.cap === "view" && <span className="muted small block">proef verlopen: alleen bekijken</span>}
                </td>
                <td>
                  {r.status === "trial" ? (
                    <input
                      type="date"
                      aria-label={`Proef ${MODULE_LABELS[r.module]} tot`}
                      value={r.trialEndsAt}
                      onChange={(e) => set(i, { trialEndsAt: e.target.value })}
                    />
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                <td>
                  <input
                    inputMode="decimal"
                    aria-label={`Prijs ${MODULE_LABELS[r.module]}`}
                    value={r.price}
                    placeholder="bijv. 12,50"
                    onChange={(e) => set(i, { price: e.target.value })}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div>
        <button
          type="button"
          className="btn primary"
          disabled={save.isPending || badPrice}
          onClick={() => save.mutate()}
        >
          Modules opslaan
        </button>
      </div>
    </section>
  );
}

function SubscriptionPanel({
  o,
  busy,
  onSave,
}: {
  o: PlatformOrgDetail;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
}) {
  const [status, setStatus] = useState<OrgStatus>(o.status);
  const [trialEnd, setTrialEnd] = useState(o.trialEndsAt ? o.trialEndsAt.slice(0, 10) : "");
  const [price, setPrice] = useState(euroInput(o.pricePerBuildingCents));
  const cents = parseEuro(price);
  function submit(e: FormEvent) {
    e.preventDefault();
    if (cents === null) return;
    onSave({ status, trialEndsAt: status === "trial" ? trialEnd || null : null, pricePerBuildingCents: cents });
  }
  return (
    <section className="panel" aria-labelledby="pf-sub">
      <h2 id="pf-sub">Abonnement</h2>
      {o.readOnly && (
        <div className="alert" role="status">
          Deze organisatie staat op alleen lezen: bekijken en exporteren kan, vastleggen niet.
        </div>
      )}
      <form className="stack" onSubmit={submit}>
        <div className="pf-row">
          <label className="field">
            <span className="label">Status</span>
            <select value={status} onChange={(e) => setStatus(e.target.value as OrgStatus)}>
              {ORG_STATUSES.map((s) => (
                <option key={s} value={s}>
                  {ORG_STATUS_LABELS[s]}
                </option>
              ))}
            </select>
          </label>
          {status === "trial" && (
            <label className="field">
              <span className="label">Proef tot en met</span>
              <input type="date" value={trialEnd} onChange={(e) => setTrialEnd(e.target.value)} />
            </label>
          )}
          <label className="field">
            <span className="label">Prijs per gebouw per maand</span>
            <input
              inputMode="decimal"
              value={price}
              onChange={(e) => setPrice(e.target.value)}
              aria-invalid={cents === null}
            />
          </label>
        </div>
        <p className="muted small">
          {o.buildings} actieve {o.buildings === 1 ? "gebouw" : "gebouwen"} × {formatEuro(cents ?? 0)} ={" "}
          <strong data-testid="sub-monthly">{formatEuro(o.buildings * (cents ?? 0))}</strong> per maand, excl. btw.
          Gearchiveerde gebouwen tellen niet mee. Alleen lezen: opgezegd, op "Alleen lezen" gezet, of proef verlopen.
        </p>
        <div>
          <button type="submit" className="btn primary" disabled={busy || cents === null}>
            Abonnement opslaan
          </button>
        </div>
      </form>
    </section>
  );
}

function InvoicesPanel({ o, onNew, onChanged }: { o: PlatformOrgDetail; onNew: () => void; onChanged: () => void }) {
  const toast = useToast();
  const setStatus = useMutation({
    mutationFn: (v: { inv: InvoiceRow; status: "open" | "paid" }) =>
      api(`/platform/invoices/${v.inv.id}`, { method: "PATCH", body: { status: v.status } }),
    onSuccess: (_r, v) => {
      toast(v.status === "paid" ? "Factuur betaald" : "Factuur weer open");
      onChanged();
    },
    onError: (e) => toast(errMsg(e)),
  });
  const remove = useMutation({
    mutationFn: (inv: InvoiceRow) => api(`/platform/invoices/${inv.id}`, { method: "DELETE" }),
    onSuccess: () => {
      toast("Factuur verwijderd");
      onChanged();
    },
    onError: (e) => toast(errMsg(e)),
  });
  const label = (i: InvoiceRow) => i.number || `${fmtDate(i.periodStart)} – ${fmtDate(i.periodEnd)}`;
  return (
    <section className="panel" aria-labelledby="pf-inv">
      <div className="titlerow">
        <h2 id="pf-inv">Facturen</h2>
        <button type="button" className="btn" onClick={onNew}>
          <IconReceipt size={18} aria-hidden="true" />
          Factuur vastleggen
        </button>
      </div>
      <p className="muted small">
        Je maakt de factuur in je eigen boekhouding; hier houd je bij wat er gefactureerd en betaald is.
      </p>
      {o.invoices.length === 0 ? (
        <p className="muted">Nog geen facturen.</p>
      ) : (
        <div className="tscroll">
          <table className="it">
            <thead>
              <tr>
                <th>Nummer</th>
                <th>Periode</th>
                <th>Gebouwen</th>
                <th>Bedrag</th>
                <th>Datum</th>
                <th>Status</th>
                <th>
                  <span className="sr-only">Acties</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {o.invoices.map((i) => (
                <tr key={i.id}>
                  <td className="code">{i.number || "—"}</td>
                  <td>
                    {fmtDate(i.periodStart)} – {fmtDate(i.periodEnd)}
                  </td>
                  <td className="num">{i.buildings}</td>
                  <td className="num">{formatEuro(i.amountCents)}</td>
                  <td>{fmtDate(i.issuedOn)}</td>
                  <td>
                    <span className={`chip ${i.status === "paid" ? "st-opgelost" : "st-open"}`}>
                      {INVOICE_STATUS_LABELS[i.status]}
                      {i.paidOn && ` ${fmtDate(i.paidOn)}`}
                    </span>
                  </td>
                  <td>
                    <span className="btnrow">
                      <button
                        type="button"
                        className="btn small"
                        aria-label={`${label(i)}: ${i.status === "paid" ? "weer open zetten" : "betaald"}`}
                        onClick={() => setStatus.mutate({ inv: i, status: i.status === "paid" ? "open" : "paid" })}
                      >
                        {i.status === "paid" ? "Weer open" : "Betaald"}
                      </button>
                      <button
                        type="button"
                        className="btn small ghost ico"
                        aria-label={`${label(i)} verwijderen`}
                        onClick={() => {
                          if (window.confirm("Deze factuur uit het overzicht verwijderen?")) remove.mutate(i);
                        }}
                      >
                        <IconTrash size={16} aria-hidden="true" />
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Vorige kalendermaand als standaardperiode. */
function lastMonth() {
  const now = new Date();
  const start = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
  const end = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return { start: isoDay(start), end: isoDay(end) };
}

function InvoiceDialog({ o, onClose, onSaved }: { o: PlatformOrgDetail; onClose: () => void; onSaved: () => void }) {
  const toast = useToast();
  const period = lastMonth();
  const [number, setNumber] = useState("");
  const [start, setStart] = useState(period.start);
  const [end, setEnd] = useState(period.end);
  const [buildings, setBuildings] = useState(String(o.buildings));
  const [amount, setAmount] = useState(euroInput(o.buildings * o.pricePerBuildingCents));
  const [issued, setIssued] = useState(isoDay(new Date()));
  const [note, setNote] = useState("");
  const [error, setError] = useState<string | null>(null);
  const save = useMutation({
    mutationFn: () => {
      const cents = parseEuro(amount);
      if (cents === null) throw new ApiError(400, "amount", "Ongeldig bedrag");
      return api(`/platform/orgs/${o.id}/invoices`, {
        method: "POST",
        body: {
          number,
          periodStart: start,
          periodEnd: end,
          buildings: Number(buildings) || 0,
          amountCents: cents,
          issuedOn: issued,
          note,
        },
      });
    },
    onSuccess: () => {
      toast("Factuur vastgelegd");
      onSaved();
    },
    onError: (e) => setError(errMsg(e)),
  });
  return (
    <Modal
      title="Factuur vastleggen"
      onClose={onClose}
      busy={save.isPending}
      footer={
        <>
          <button type="button" className="btn ghost" onClick={onClose}>
            Annuleren
          </button>
          <button type="submit" form="invoice-form" className="btn primary" disabled={save.isPending}>
            Vastleggen
          </button>
        </>
      }
    >
      <form
        id="invoice-form"
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          setError(null);
          save.mutate();
        }}
      >
        {error && (
          <div className="alert" role="alert">
            {error}
          </div>
        )}
        <label className="field">
          <span className="label">Factuurnummer (uit je boekhouding)</span>
          <input value={number} onChange={(e) => setNumber(e.target.value)} />
        </label>
        <div className="pf-row">
          <label className="field">
            <span className="label">Periode van</span>
            <input type="date" required value={start} onChange={(e) => setStart(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">tot en met</span>
            <input type="date" required value={end} onChange={(e) => setEnd(e.target.value)} />
          </label>
        </div>
        <div className="pf-row">
          <label className="field">
            <span className="label">Gebouwen</span>
            <input
              inputMode="numeric"
              value={buildings}
              onChange={(e) => {
                setBuildings(e.target.value);
                const n = Number(e.target.value);
                if (Number.isInteger(n) && n >= 0) setAmount(euroInput(n * o.pricePerBuildingCents));
              }}
            />
          </label>
          <label className="field">
            <span className="label">Bedrag excl. btw</span>
            <input inputMode="decimal" required value={amount} onChange={(e) => setAmount(e.target.value)} />
          </label>
        </div>
        <label className="field">
          <span className="label">Factuurdatum</span>
          <input type="date" required value={issued} onChange={(e) => setIssued(e.target.value)} />
        </label>
        <label className="field">
          <span className="label">Notitie</span>
          <input value={note} onChange={(e) => setNote(e.target.value)} />
        </label>
      </form>
    </Modal>
  );
}

const BILLING_FIELDS: [keyof BillingDetails, string, string?][] = [
  ["company", "Bedrijfsnaam"],
  ["contact", "Contactpersoon"],
  ["email", "E-mail voor facturen", "email"],
  ["address", "Adres"],
  ["postcode", "Postcode"],
  ["city", "Plaats"],
  ["kvk", "KvK-nummer"],
  ["vat", "Btw-nummer"],
  ["reference", "Referentie / inkoopnummer"],
];

function BillingPanel({
  billing,
  busy,
  onSave,
}: {
  billing: BillingDetails;
  busy: boolean;
  onSave: (b: BillingDetails) => void;
}) {
  const [b, setB] = useState(billing);
  return (
    <section className="panel" aria-labelledby="pf-billing">
      <h2 id="pf-billing">Factuurgegevens</h2>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onSave(b);
        }}
      >
        <div className="formgrid">
          {BILLING_FIELDS.map(([k, label, type]) => (
            <label key={k} className="field">
              <span className="label">{label}</span>
              <input type={type ?? "text"} value={b[k]} onChange={(e) => setB({ ...b, [k]: e.target.value })} />
            </label>
          ))}
        </div>
        <label className="field">
          <span className="label">Notities (intern)</span>
          <textarea rows={3} value={b.notes} onChange={(e) => setB({ ...b, notes: e.target.value })} />
        </label>
        <div>
          <button type="submit" className="btn primary" disabled={busy}>
            Factuurgegevens opslaan
          </button>
        </div>
      </form>
    </section>
  );
}

function AccessPanel({
  o,
  busy,
  onSave,
  onInvited,
}: {
  o: PlatformOrgDetail;
  busy: boolean;
  onSave: (body: Record<string, unknown>) => void;
  onInvited: (o: PlatformOrgDetail) => void;
}) {
  const toast = useToast();
  const [name, setName] = useState(o.name);
  const [domains, setDomains] = useState(domainsText(o.allowedDomains));
  const [reportBrand, setReportBrand] = useState<ReportBrand>(o.reportBrand);
  const [elementCoding, setElementCoding] = useState(o.elementCoding);
  const [email, setEmail] = useState("");
  const invite = useMutation({
    mutationFn: () =>
      api<{ org: PlatformOrgDetail; mailed: boolean }>(`/platform/orgs/${o.id}/owner-invitations`, {
        method: "POST",
        body: { email },
      }),
    onSuccess: (r) => {
      onInvited(r.org);
      toast(r.mailed ? `Uitnodiging gestuurd naar ${email}` : `${email} kan inloggen met Google`);
      setEmail("");
    },
    onError: (e) => toast(errMsg(e)),
  });
  return (
    <section className="panel" aria-labelledby="pf-access">
      <h2 id="pf-access">Organisatie en toegang</h2>
      <form
        className="stack"
        onSubmit={(e) => {
          e.preventDefault();
          onSave({ name, allowedDomains: parseDomains(domains), reportBrand, elementCoding });
        }}
      >
        <div className="pf-row">
          <label className="field">
            <span className="label">Naam</span>
            <input required value={name} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field">
            <span className="label">Toegestane domeinen</span>
            <input value={domains} onChange={(e) => setDomains(e.target.value)} placeholder="bedrijf.nl" />
          </label>
          <label className="field">
            <span className="label">Huisstijl rapport</span>
            <select value={reportBrand} onChange={(e) => setReportBrand(e.target.value as ReportBrand)}>
              {REPORT_BRANDS.map((r) => (
                <option key={r} value={r}>
                  {REPORT_BRAND_LABELS[r]}
                </option>
              ))}
            </select>
          </label>
        </div>
        <label className="check">
          <input type="checkbox" checked={elementCoding} onChange={(e) => setElementCoding(e.target.checked)} />
          <span>
            Volledige NLBE-SfB-tabel (sub- en detailcodes)
            <span className="muted small block">
              Uit: alleen elementen met 2 cijfers. Gebruiksrechten van de tabel eerst navragen bij Ketenstandaard.
            </span>
          </span>
        </label>
        <p className="muted small">
          Het PDF-rapport gaat naar opdrachtgevers en draagt de huisstijl van de organisatie; de app zelf is altijd
          Klantpunt. Met een toegestaan domein komt iedereen met een Google-account op dat domein binnen als inspecteur.
          Leeg = alleen uitgenodigde adressen.
        </p>
        <div>
          <button type="submit" className="btn primary" disabled={busy}>
            Opslaan
          </button>
        </div>
      </form>

      <h3 className="pf-sub">Eigenaren</h3>
      <ul className="list">
        {o.owners.map((u) => (
          <li key={u.userId}>
            <span className="grow">
              {u.name ? (
                <>
                  <strong>{u.name}</strong> <span className="muted">{u.email}</span>
                </>
              ) : (
                u.email
              )}
            </span>
          </li>
        ))}
        {o.ownerInvitations.map((i) => (
          <li key={i.id}>
            <span className="grow">{i.email}</span>
            <span className="chip">Uitgenodigd tot {fmtDate(i.expiresAt)}</span>
          </li>
        ))}
        {o.owners.length === 0 && o.ownerInvitations.length === 0 && <li className="muted">Nog geen eigenaar.</li>}
      </ul>
      <form
        className="pf-row"
        onSubmit={(e) => {
          e.preventDefault();
          invite.mutate();
        }}
      >
        <label className="field">
          <span className="label">Eigenaar uitnodigen</span>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="naam@bedrijf.nl"
          />
        </label>
        <button type="submit" className="btn" disabled={invite.isPending}>
          Uitnodigen
        </button>
      </form>
    </section>
  );
}
